import { z } from 'zod';
import { TxType } from './tx-type';

/**
 * 트랜잭션 종료 상태.
 *
 * `timeout`·`cancelled`는 계약에만 있고 **아직 아무도 만들지 않는다**. 서버 normalizer는
 * OTel span status를 따라 `ok`/`error`만 낸다. PDF 30초 상한이나 SSE 연결 끊김을 따로
 * 세게 되면 그때 채운다. 화면·검색은 세 단계 판정(`@apm/core`의 `statusLevel`)을 쓰므로
 * 값이 늘어도 그대로 돈다.
 */
export const TxStatus = z.enum(['ok', 'error', 'timeout', 'cancelled']);
export type TxStatus = z.infer<typeof TxStatus>;

/**
 * 끝난 트랜잭션 한 건의 요약. 서버 normalizer가 OTel span 트리에서 만든다.
 * 개인정보는 여기 들어오기 전에 제거된다(규칙 4). 사용자 식별자는 이 스키마에 두지 않는다
 * (2026-09-07 결정 2). 클라이언트 IP·User-Agent는 결정 3·11로 저장한다.
 */
export const TxSummary = z.object({
  /** W3C trace-id. 32자 소문자 hex */
  traceId: z.string().regex(/^[0-9a-f]{32}$/),
  /** 트랜잭션 id. root span id를 그대로 쓴다. 16자 소문자 hex */
  txId: z.string().regex(/^[0-9a-f]{16}$/),
  /** OTel service.name (예: oneworks2-api) */
  service: z.string().min(1),
  /**
   * OTel `service.instance.id`. 어느 프로세스에서 난 요청인지 (16회차).
   * 인스턴스별 오류 집계와 외부 API가 이것으로 나눈다. Resource에 값이 없으면 비어 있다.
   * 16회차 전에 저장한 파일에는 이 필드가 없다.
   */
  instance: z.string().optional(),
  /**
   * 이 요청을 부른 **다른 서비스**의 이름 (26회차).
   *
   * A가 B를 부르면 `traceparent`가 전파돼 B의 root span에 원격 부모가 실린다. 그 부모 span을
   * 가진 서비스를 알 수 있으면 여기에 적는다. 정규화 누적기에 부모 쪽 trace가 이미 확정돼
   * 사라진 뒤면 알 수 없으므로 **선택**이다. 같은 서비스 안의 루프백(MCP 자기 호출)에는 없다.
   * 26회차 전에 저장한 파일에는 이 필드가 없다. 조회는 `UNION ALL BY NAME`이라 NULL로 온다
   */
  parentService: z.string().optional(),
  txType: TxType,
  /** 진입점. http는 "METHOD /route", worker는 워커 이름 */
  entry: z.string().min(1),
  /** 시작 시각. epoch ms */
  startedAt: z.number().int().nonnegative(),
  durationMs: z.number().nonnegative(),
  status: TxStatus,
  httpStatus: z.number().int().min(100).max(599).optional(),
  /** 예외 클래스명 등 */
  errorType: z.string().optional(),
  /**
   * 예외 메시지 (2026-09-07 결정 3). 사내 관제 도구라 저장한다. 길면 앞 1024자만 남긴다.
   * `errorType`과 **같은 span**에서 가져온다. 둘이 다른 span에서 오면 상세가 어긋난다.
   * 16회차 전에 저장한 파일에는 이 필드가 없다. 조회는 `UNION ALL BY NAME`이라 NULL로 온다.
   */
  errorMessage: z.string().max(1024).optional(),
  tenantId: z.string().optional(),
  sqlCount: z.number().int().nonnegative(),
  /**
   * 그중 트랜잭션 제어·세션 설정 문장 수 (23-A회차, 2026-09-08 사용자 확정 22).
   *
   * `BEGIN`·`COMMIT`과 `SELECT set_config('app.tenant_id', $1, true)` 같은 문장이다. DB 왕복이라
   * `sqlCount`에는 그대로 들어가지만, "요청 한 건이 DB를 몇 번 부르는가"를 볼 때는 빼야 한다 —
   * OneWorks는 트랜잭션마다 회사 구분 값을 넣어 이 값이 요청마다 서넛씩 붙는다.
   * 판정은 `@apm/core`의 `isTxControlSql`·`isSessionSql`이고 상세 화면이 접는 판정과 같다.
   * 23-A회차 전에 저장한 파일에는 이 필드가 없다. 조회는 `UNION ALL BY NAME`이라 NULL로 온다
   */
  sqlControlCount: z.number().int().nonnegative().optional(),
  sqlTimeMs: z.number().nonnegative(),
  /** 외부 HTTP 호출(undici) 합산 시간 */
  extTimeMs: z.number().nonnegative(),
  /**
   * 실패한 SQL 건수 (7회차 결정 1). 6회차까지 저장한 파일에는 이 필드가 없으므로
   * 기본값 0으로 읽는다. 화면은 이 값이 1 이상이면 상태를 최소 '실패'로 본다(결정 5).
   */
  sqlErrorCount: z.number().int().nonnegative().default(0),
  /** 첫 DB 오류 코드(SQLSTATE). 자세한 건 `SpanRecord`에 있다 */
  sqlErrorCode: z.string().optional(),
  /**
   * 클라이언트 IP (2026-09-07 결정 3·11). 사내 관제 도구라 개인정보로 보지 않는다.
   * 15회차 전에 저장한 파일에는 이 필드가 없다. 조회는 `UNION ALL BY NAME`이라 NULL로 온다.
   */
  clientAddress: z.string().optional(),
  /** User-Agent 원문 (2026-09-07 결정 3·11). 같은 이유로 옛 파일에는 없다 */
  userAgent: z.string().optional(),
  /**
   * 이 요청을 보낸 화면(Referer 헤더, 22-C회차 UI 피드백 8). 서명 토큰(`st=`)은 에이전트·서버가
   * 지운 뒤라 여기 없다. 길면 앞 2048자. 22-C회차 전에 저장한 파일에는 이 필드가 없다
   */
  referer: z.string().max(2048).optional(),
  /** 응답 본문 크기(바이트, `content-length`). 청크 전송이면 없다 (22-C회차 UI 피드백 10) */
  responseBytes: z.number().int().nonnegative().optional(),
  /**
   * 5xx로 끝난 요청의 스택 상위 프레임 (25회차, 조정자 제안 7).
   *
   * 에이전트가 **HTTP 상태 500 이상일 때만** 앞 5개를 실어 보낸다. 4xx·정상 요청에는 없다.
   * 프레임 하나는 200자까지고 `node_modules` 프레임은 빠져 있다(앱 코드가 먼저 보이게).
   * 스택 원문은 어느 경로로도 저장하지 않는다 — 여기 있는 몇 줄이 전부다.
   * 25회차 전에 저장한 파일에는 이 필드가 없다. 조회는 `UNION ALL BY NAME`이라 NULL로 온다
   */
  errorFrames: z.array(z.string().max(200)).max(5).optional(),
});
export type TxSummary = z.infer<typeof TxSummary>;
